import { colorForSpeed, parsePaletteKey } from '../../lib/wind/palette';

const css = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
const cssA = ([r, g, b, a]) => `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`;

const decimalsOf = (x) => {
  const s = String(x);
  const i = s.indexOf('.');
  return i < 0 ? 0 : s.length - i - 1;
};

// Diverging banded scale for scalar products (convergence/ridge): one swatch
// per captured isoband colour (incl. the transparent neutral band), labelled
// by the band's lower edge, overflow on top as ">max". Convergence edges sit
// at x.x5 (min −0.25, step 0.1) so label precision follows the scale.
const scalarStops = (product) => {
  const colors = parsePaletteKey(product.fillPaletteKey);
  const { min, max } = product.range;
  const d = Math.max(decimalsOf(min), decimalsOf(product.step));
  const fmt = (v) => v.toFixed(d);
  return colors
    .map((c, i) => ({
      label: i === colors.length - 1 ? `>${fmt(max)}` : fmt(min + i * product.step),
      color: cssA(c),
    }))
    .reverse();
};

// Vertical wind-speed color scale (mirrors OverlayLegend). Integer m/s labels
// down from fillRange.max, top bin shown as ">max", colours from the shared
// Skysight legend ramp.
const WindLegend = ({ product }) => {
  if (!product || (!product.fillRange && product.kind !== 'scalar')) return null;
  let stops;
  if (product.kind === 'scalar') {
    stops = scalarStops(product);
  } else {
    const top = product.fillRange.max;
    stops = [];
    for (let v = top; v >= 0; v--) {
      stops.push({
        label: v === top ? `>${v}` : String(v),
        color: css(colorForSpeed(v, top)),
      });
    }
  }

  // Styling mirrors OverlayLegend: slim translucent strip on mobile (doesn't
  // block the map), larger swatches/labels with row spacing on desktop (sm:).
  return (
    <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 z-[5] rounded sm:rounded-md bg-slate-900/60 sm:bg-slate-900/65 backdrop-blur-md shadow sm:shadow-md ring-1 ring-white/10 px-1.5 py-1.5 sm:px-2.5 sm:py-2.5 text-[9px] sm:text-[11px] leading-tight">
      <ul className="m-0 p-0 list-none space-y-0 sm:space-y-0.5">
        {stops.map((s) => (
          <li key={s.label} className="flex items-center gap-1 sm:gap-1.5">
            <span
              // scalar palettes include transparent bands — outline the swatch
              // so "no colour drawn" still reads as a band in the legend.
              className={`inline-block w-3 h-2 sm:w-5 sm:h-3.5 rounded-[2px] sm:rounded-sm shrink-0 ${product.kind === 'scalar' ? 'ring-1 ring-inset ring-white/30' : ''}`}
              style={{ backgroundColor: s.color }}
            />
            <span className="text-slate-100 tabular-nums">{s.label}</span>
          </li>
        ))}
      </ul>
      <div className="mt-0.5 sm:mt-1.5 text-center font-medium text-slate-300">m/s</div>
    </div>
  );
};

export default WindLegend;
